/**
 * Calendar shaping for training runs — NC-1269 Slice 1.
 *
 * The tutor's request was a year calendar of all trainings. The whole point of
 * putting runs on a calendar rather than in a list is to make **double bookings
 * of a room visible**, so overlap detection is a first-class part of this module
 * rather than a rendering afterthought.
 *
 * All of this is pure: the page renders what these functions return, and every
 * rule below is unit-tested without a DOM. Slice 1 needs no API change — the
 * runs list already carries period, room, participants, workload and cost
 * center (verified field by field in the issue's scoping comment).
 */

import { Run } from '../services/types';
import { parseDateSafe, isDateOnlyIso } from './dateFormat';

/** A run placed on the calendar, with its resolved period. */
export interface CalendarEntry {
  run: Run;
  runId: string;
  start: Date;
  end: Date;
  /**
   * RunIds of other entries that share this run's room AND overlap in time.
   * Empty for the common case; non-empty is what the UI marks.
   */
  conflictsWith: string[];
}

export interface CalendarMonth {
  /** 0-11 */
  month: number;
  entries: CalendarEntry[];
}

export interface CalendarYear {
  year: number;
  /** Entries touching this year, earliest start first. */
  entries: CalendarEntry[];
  /** Always 12 buckets, so the UI can render an empty month without a lookup. */
  months: CalendarMonth[];
  /** Runs that carry no usable start date and therefore cannot be placed. */
  unplaceable: Run[];
}

const MS_PER_DAY = 86_400_000;

/**
 * Resolve the period a run occupies.
 *
 * Start precedence follows NC-457: the datetime fields win, then the date-only
 * planning fields, then creation. `createdAtUtc` is the last resort so a legacy
 * run without any planning data still shows up somewhere truthful instead of
 * vanishing from the calendar.
 *
 * A date-only end (`expireOn` is `yyyy-MM-dd`) is stretched to the END of that
 * day. That matters for conflict detection: with a bare midnight, a run ending
 * on the 10th and another starting on the 10th would look disjoint, when in
 * practice they collide for a whole day. Erring toward "flag it" is the right
 * bias for a view whose job is to surface double bookings.
 */
export function resolveRunPeriod(run: Run): { start: Date; end: Date } | null {
  const startRaw = run.startDateTimeUtc || run.scheduledStart || run.createdAtUtc;
  const start = parseDateSafe(startRaw);
  if (!start) return null;

  const endRaw = run.endDateTimeUtc || run.expireOn;
  const parsedEnd = parseDateSafe(endRaw);
  let end = parsedEnd;
  if (end && isDateOnlyIso(endRaw)) {
    end = new Date(end.getTime() + MS_PER_DAY - 1);
  }

  // No end, or an end before the start (sparse/hand-edited docs): collapse to a
  // single day rather than inventing a duration or dropping the run.
  if (!end || end.getTime() < start.getTime()) {
    end = new Date(start.getTime() + MS_PER_DAY - 1);
  }
  return { start, end };
}

/** True when two closed intervals share at least one instant. */
export function periodsOverlap(
  a: { start: Date; end: Date },
  b: { start: Date; end: Date },
): boolean {
  return a.start.getTime() <= b.end.getTime() && b.start.getTime() <= a.end.getTime();
}

/**
 * Normalised room key, or null when the run has no room.
 *
 * A run without `roomId` must never collide with another room-less run — "no
 * room yet" is not a shared resource. Returning null (instead of grouping on
 * `''`) is what keeps that from happening.
 */
function roomKey(run: Run): string | null {
  const id = typeof run.roomId === 'string' ? run.roomId.trim() : '';
  return id === '' ? null : id.toLowerCase();
}

/**
 * Annotate entries with the RunIds they collide with (same room, overlapping
 * time). O(n²) per room, which is the right trade at the scale of one year of
 * trainings and keeps the rule readable.
 */
export function markRoomConflicts(entries: CalendarEntry[]): CalendarEntry[] {
  const byRoom = new Map<string, CalendarEntry[]>();
  for (const entry of entries) {
    const key = roomKey(entry.run);
    if (!key) continue;
    const bucket = byRoom.get(key);
    if (bucket) bucket.push(entry);
    else byRoom.set(key, [entry]);
  }

  const conflicts = new Map<string, Set<string>>();
  for (const bucket of byRoom.values()) {
    for (let i = 0; i < bucket.length; i += 1) {
      for (let j = i + 1; j < bucket.length; j += 1) {
        const a = bucket[i];
        const b = bucket[j];
        if (a.runId === b.runId) continue;
        if (!periodsOverlap(a, b)) continue;
        if (!conflicts.has(a.runId)) conflicts.set(a.runId, new Set());
        if (!conflicts.has(b.runId)) conflicts.set(b.runId, new Set());
        conflicts.get(a.runId)!.add(b.runId);
        conflicts.get(b.runId)!.add(a.runId);
      }
    }
  }

  return entries.map((entry) => ({
    ...entry,
    conflictsWith: Array.from(conflicts.get(entry.runId) ?? []).sort(),
  }));
}

/** Months (0-11) of `year` that the period touches. Empty if it misses the year. */
export function monthsInYear(period: { start: Date; end: Date }, year: number): number[] {
  const startYear = period.start.getFullYear();
  const endYear = period.end.getFullYear();
  if (endYear < year || startYear > year) return [];

  const from = startYear === year ? period.start.getMonth() : 0;
  const to = endYear === year ? period.end.getMonth() : 11;
  const months: number[] = [];
  for (let m = from; m <= to; m += 1) months.push(m);
  return months;
}

/**
 * Every year that has at least one placeable run, plus `currentYear`, ascending.
 *
 * A run spanning New Year's Eve contributes BOTH of its years — the issue's
 * edge case "visible in both years" starts here, at the year picker: a year the
 * selector does not offer cannot show anything.
 */
export function availableYears(runs: Run[], currentYear: number): number[] {
  const years = new Set<number>([currentYear]);
  for (const run of runs) {
    const period = resolveRunPeriod(run);
    if (!period) continue;
    for (let y = period.start.getFullYear(); y <= period.end.getFullYear(); y += 1) {
      years.add(y);
    }
  }
  return Array.from(years).sort((a, b) => a - b);
}

/**
 * Shape one year: which runs touch it, bucketed per month, conflicts marked.
 *
 * Conflict detection runs across the WHOLE year's entries, not per month, so a
 * collision between a run ending in March and one starting in March is found
 * once and shown in every month both runs appear in.
 */
export function buildCalendarYear(runs: Run[], year: number): CalendarYear {
  const entries: CalendarEntry[] = [];
  const unplaceable: Run[] = [];

  for (const run of runs) {
    const period = resolveRunPeriod(run);
    if (!period) {
      unplaceable.push(run);
      continue;
    }
    if (monthsInYear(period, year).length === 0) continue;
    entries.push({
      run,
      runId: run.runId || run.id || '',
      start: period.start,
      end: period.end,
      conflictsWith: [],
    });
  }

  const marked = markRoomConflicts(entries).sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );

  const months: CalendarMonth[] = Array.from({ length: 12 }, (_, month) => ({
    month,
    entries: marked.filter((entry) => monthsInYear(entry, year).includes(month)),
  }));

  return { year, entries: marked, months, unplaceable };
}
